import type { GetServerSideProps, InferGetServerSidePropsType } from 'next';

import { FiltersReducerState } from '@/components/History/OrderHistory/Filters/reducer';

import { isAdvancedClosedOrderStatus } from '@/constants/advancedOrder';
import { SUPPORTED_CHAIN_IDS_LIMIT_ORDERS } from '@/constants/chain';

import { Header } from '../../components/Header';
import { History, DIVIDER_CHAR, SUPPORTED_TABS, TradeHistoryProps } from '../../components/History';

type TradeHistoryPageProps = TradeHistoryProps;

const TradeHistory = ({
  selectedTab,
  initialPage,
  initialFilters,
  initialSearchTerm,
  initialOpenOrderChainId,
}: InferGetServerSidePropsType<typeof getServerSideProps>) => {
  return (
    <>
      <Header />
      <History
        selectedTab={selectedTab}
        initialPage={initialPage}
        initialFilters={initialFilters}
        initialSearchTerm={initialSearchTerm}
        initialOpenOrderChainId={initialOpenOrderChainId}
      />
    </>
  );
};

// Parse pagination and filters parameters in the url
export const getServerSideProps = (async ({ query }) => {
  const tabParam = query.tab && typeof query.tab === 'object' ? query.tab[0] : undefined;
  const selectedTab: TradeHistoryProps['selectedTab'] =
    tabParam && (SUPPORTED_TABS as readonly string[]).includes(tabParam)
      ? (tabParam as TradeHistoryProps['selectedTab'])
      : 'swaps';

  const initialPage = !query.page ? undefined : Number(query.page as string);

  const time = !query.time ? 0 : Number(query.time as string);
  const networks =
    !query.networks || typeof query.networks !== 'string'
      ? []
      : query.networks.split(DIVIDER_CHAR).filter((n) => !!n);
  const orderTypes =
    !query.orderTypes || typeof query.orderTypes !== 'string'
      ? []
      : query.orderTypes.split(DIVIDER_CHAR).filter((n) => !!n);
  const status =
    selectedTab === 'limit-orders' && isAdvancedClosedOrderStatus(query.advancedOrderStatus)
      ? query.advancedOrderStatus
      : undefined;

  const initialFilters: FiltersReducerState | undefined =
    !time && !networks.length && !orderTypes.length && !status
      ? undefined
      : {
          time,
          networks,
          orderTypes,
          status,
        };

  const initialSearchTerm = typeof query.searchTerm === 'string' ? query.searchTerm : undefined;

  // Use the first supported network from the URL, or all networks when absent/invalid.
  const supportedNetworksForOpenOrders = networks
    .map((network) => Number(network))
    .filter(
      (chainId) => Number.isFinite(chainId) && SUPPORTED_CHAIN_IDS_LIMIT_ORDERS.includes(chainId),
    );
  const initialOpenOrderChainId =
    supportedNetworksForOpenOrders.length > 0 ? supportedNetworksForOpenOrders[0] : null;

  return {
    props: {
      selectedTab,
      ...(initialPage && { initialPage }),
      ...(initialFilters && { initialFilters }),
      ...(initialSearchTerm && { initialSearchTerm }),
      initialOpenOrderChainId,
    },
  };
}) satisfies GetServerSideProps<TradeHistoryPageProps>;

export default TradeHistory;
